import type { Branch } from '@interfaces/financialProduct';
import type { TranslatedString } from '@interfaces/shared';
import type { DraggableProvided } from 'react-beautiful-dnd';

export interface Security extends Branch {
  /** Deprecated: colours render from the org asset class registry at display time. */
  color?:
    | 'supportOne'
    | 'supportTwo'
    | 'supportThree'
    | 'supportFour'
    | 'supportFive'
    | 'supportSix';
}

export type ColumnConfig = {
  enabled: boolean;
  label: TranslatedString;
};

export type SecurityTableColumns = {
  security: ColumnConfig;
  assetClass: ColumnConfig;
  taxRank: ColumnConfig;
  targetPercentage: ColumnConfig;
  portfolioPercentage: ColumnConfig;
  buttonRemoveSecurity: {
    enabled: boolean;
  };
};

export type SecurityTableOptions = {
  securityAllocationChart: {
    columns: [SecurityTableColumns];
  };
};

export type DragProps = {
  innerRef: DraggableProvided['innerRef'];
  draggableProps: DraggableProvided['draggableProps'];
  dragHandleProps: DraggableProvided['dragHandleProps'];
  /** When drag is disabled, still provides a handle node for react-beautiful-dnd (flat mode). */
  rowHandleProps?: DraggableProvided['dragHandleProps'];
  isDragging: boolean;
};

type ColumnConfigKey = Exclude<
  keyof SecurityTableColumns,
  'buttonRemoveSecurity'
>;

type ColumnDef = {
  width: string;
  key: ColumnConfigKey;
  sortKey: string;
  /** Read by the heading, the rows and the total, so a column reads as one. */
  align: 'left' | 'center' | 'right';
};

export type { ColumnConfigKey, ColumnDef };

/**
 * Tax rank holds a one or two digit number, where target percentage holds a
 * number field and the remove button beside it, so the room is given to the
 * column that has something to put in it.
 */
export const COLUMNS: Record<string, ColumnDef> = {
  SECURITY: {
    width: '50%',
    key: 'security',
    sortKey: 'financialProduct.ticker',
    align: 'left',
  },
  // A badge and a single figure are each narrower than the heading above them,
  // so they sit in the middle of the column rather than adrift at one end.
  ASSET_CLASS: {
    width: '20%',
    key: 'assetClass',
    sortKey: 'financialProduct.primaryAssetClass.name',
    align: 'center',
  },
  TAX_RANK: {
    width: '10%',
    key: 'taxRank',
    sortKey: 'taxRank',
    align: 'center',
  },
  // Weights are read down the column against each other and have to add up to
  // a hundred, so they hang off the decimal point rather than the left margin.
  TARGET_PERCENTAGE: {
    width: '20%',
    key: 'targetPercentage',
    sortKey: 'percentage',
    align: 'right',
  },
  PORTFOLIO_PERCENTAGE: {
    width: '15%',
    key: 'portfolioPercentage',
    sortKey: 'effectivePercentage',
    align: 'right',
  },
};

/** Grouped mode opens with a gutter where the flat asset-class column sat. */
export const GROUPED_MODE_SPACER_WIDTH = '20%' as const;

export type SecuritiesTableLayout = 'flat' | 'grouped' | 'groupedWithPortfolio';

const GROUPED_WITH_PORTFOLIO_COLUMN_WIDTHS: Partial<
  Record<ColumnConfigKey, string>
> = {
  security: '40%',
  taxRank: '10%',
  targetPercentage: '15%',
  portfolioPercentage: '15%',
};

/** Column widths that sum to 100% for the layout the table is rendering. */
export const getColumnWidth = (
  column: ColumnDef,
  layout: SecuritiesTableLayout,
): string =>
  layout === 'groupedWithPortfolio'
    ? (GROUPED_WITH_PORTFOLIO_COLUMN_WIDTHS[column.key] ?? column.width)
    : column.width;

export const getSecuritiesTableLayout = ({
  isGroupedMode,
  showPortfolioPercentageColumn,
}: {
  isGroupedMode: boolean;
  showPortfolioPercentageColumn: boolean;
}): SecuritiesTableLayout => {
  if (isGroupedMode && showPortfolioPercentageColumn) {
    return 'groupedWithPortfolio';
  }

  return isGroupedMode ? 'grouped' : 'flat';
};

/**
 * The remove button sits in a lane at the right of the target percentage
 * column. The heading and the total keep the lane clear even where no button
 * is drawn, so the weights sit under their heading in every table that uses
 * this one, editable or not.
 */
export const TARGET_PERCENTAGE_ACTION_LANE = '32px' as const;

/**
 * Narrower than this and the asset class badge runs into the tax rank beside
 * it and the total is cut off the right edge, so the table is held here and
 * the screen scrolls to it instead. Sized so target percentage keeps the
 * {@link COLUMNS} share the number field and its remove button need.
 */
export const TABLE_MIN_WIDTH = '600px' as const;

export const isPortfolioPercentageColumnEnabled = (
  options?: SecurityTableOptions,
): boolean =>
  !!options?.securityAllocationChart?.columns?.[0]?.portfolioPercentage
    ?.enabled;

export const findSecurityIndex = (arr: Security[], target: Security): number =>
  arr.findIndex((s) => s.financialProduct.id === target.financialProduct.id);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getItemStyle = (_isDragging: boolean, draggableStyle: any) => ({
  userSelect: 'none',
  padding: 0.5,
  margin: 0,
  background: 'white',
  ...draggableStyle,
  height: '50px',
});
